import 'azure-maps-control/dist/atlas.min.css';
import './PopupStyles.css';
import { AuthenticationType, ControlPosition, data as atlasData } from 'azure-maps-control';
import React, { useEffect, useMemo } from 'react';
import { AzureMap, AzureMapDataSourceProvider, AzureMapLayerProvider, AzureMapPopup, AzureMapsContext, AzureMapsProvider, IAzureMapControls, IAzureMapOptions } from 'react-azure-maps';
import { PolylineProps, PushpinProps } from '../../../types/MapModels';
import { AzureMapCameraControl } from './AzureMapCameraControl';
import { SYMBOL_MARKER_OFFSET_EXPRESSION } from './AzureMapMarkerConstants';
import { AzureMapSelectionDrawingControl } from './AzureMapSelectionDrawingControl';
import { AzureMapSpriteRegistrar } from './AzureMapSpriteRegistrar';
import { ShapeSelectionMode } from './modules/selection-control/control/SelectionControl';

export interface AzureMapSelectionMapProps {
    map?: IAzureMapOptions;
    polylinesProp?: PolylineProps[];
    pushpinsProp?: PushpinProps[];
    loadBubblePins?: PushpinProps[];
    alightingsBubblePins?: PushpinProps[];
    boardBubblePins?: PushpinProps[];
    infoboxesProp?: any[];
    vehiclesProp?: any[];
    onStopClick?: (args: {
        stopId?: string;
        boarding?: number;
        alighting?: number;
        load?: number;
        title?: string;
        stopName?: string;
        lat?: number;
        lon?: number;
        routeName?: string;
        routeId?: string;
    }) => void;
    onSelectionChanged?: (selectedFeatures: atlasData.Feature<atlasData.Geometry, any>[]) => void;
    selectionSourceId?: string;
    selectionResetToken?: number;
    showRouteSegmentPopup?: boolean;
    emitSelectionArea?: boolean;
    cameraPadding?: {
        top?: number;
        bottom?: number;
        left?: number;
        right?: number;
    };
    cameraMaxZoom?: number;
    disableAutoCamera?: boolean;
    showStopHoverPopup?: boolean;
    mapMode?: 'bubble' | 'heat';
    heatMetric?: 'boarding' | 'alighting' | 'load';
    showZoomControls?: boolean;
    showBoardingsLayer?: boolean;
    showAlightingsLayer?: boolean;
    showLoadLayer?: boolean;
}

type MapBounds = {
    north: number;
    south: number;
    east: number;
    west: number;
};

const DEFAULT_CAMERA_PADDING = {
    top: 60,
    bottom: 60,
    left: 60,
    right: 60,
} as const;

const HEAT_RADIUS_BY_ZOOM = [
    'interpolate',
    ['linear'],
    ['zoom'],
    5, 15,
    16, 34,
] as any;

const HEAT_INTENSITY_BY_ZOOM = [
    'interpolate',
    ['linear'],
    ['zoom'],
    8, 0.9,
    16, 2.1,
] as any;

const HEAT_COLOR_RAMP = [
    'interpolate',
    ['linear'],
    ['heatmap-density'],
    0, 'rgba(47, 137, 252, 0)',
    0.1, '#2f89fc',
    0.55, '#f1c40f',
    0.78, '#e67e22',
    1, '#c0392b',
] as any;

function getMetricValue(options: any, metric: 'boarding' | 'alighting' | 'load'): number {
    const raw = metric === 'boarding'
        ? options?.boarding
        : metric === 'alighting'
            ? options?.alighting
            : options?.load;
    const value = Number(raw);
    if (!Number.isFinite(value) || value <= 0) {
        return 0;
    }
    return value;
}

function convertPushpinsToAzure(pushpins: PushpinProps[] = []): atlasData.Feature<atlasData.Point, any>[] {
    return pushpins.map((p) => {
        const [lat, lon] = p.location;
        const coordinates: [number, number] = [lon, lat];
        return new atlasData.Feature(
            new atlasData.Point(coordinates),
            {
                ...p.options,
                ...p.eventHandlers,
            },
        );
    });
}

function convertPolylines(
    polylines: PolylineProps[] = [],
): atlasData.Feature<atlasData.LineString, any>[] {
    return polylines.map((polyline) => {
        const azureCoords = polyline.locations.map(([lat, lon]) => [lon, lat]) as [number, number][];
        const line = new atlasData.LineString(azureCoords);
        const properties = {
            strokeColor: polyline.options?.strokeColor,
            strokeWidth: polyline.options?.strokeThickness,
            ...((polyline as any)?.options?.metadata ?? {}),
            ...(polyline.metadata ?? {}),
        };
        return new atlasData.Feature(line, properties);
    });
}

const AzureMapSelectionMap: React.FC<AzureMapSelectionMapProps> = ({
    map,
    pushpinsProp,
    polylinesProp,
    infoboxesProp,
    vehiclesProp,
    loadBubblePins,
    alightingsBubblePins,
    boardBubblePins,
    onStopClick,
    onSelectionChanged,
    selectionSourceId = 'bubblePinsDataSource',
    selectionResetToken,
    showRouteSegmentPopup = false,
    emitSelectionArea = false,
    cameraPadding,
    cameraMaxZoom,
    disableAutoCamera = false,
    showStopHoverPopup = false,
    mapMode = 'bubble',
    heatMetric = 'load',
    showZoomControls = false,
    showBoardingsLayer = true,
    showAlightingsLayer = true,
    showLoadLayer = true,
}) => {
    const defaultCenter = [-95.382669, 29.757224] as [number, number];
    const defaultZoom = 10;
    const { mapRef, isMapReady } = React.useContext(AzureMapsContext);
    const [lineHoverPopup, setLineHoverPopup] = React.useState<{ position: [number, number]; props: any } | null>(null);
    const [stopHoverPopup, setStopHoverPopup] = React.useState<{ position: [number, number]; props: any } | null>(null);
    const stopHoverKeyRef = React.useRef<string | null>(null);

    const convertedFeatures = useMemo(() => {
        return convertPushpinsToAzure(pushpinsProp);
    }, [pushpinsProp]);

    const loadBubble = useMemo(() => {
        return convertPushpinsToAzure(loadBubblePins);
    }, [loadBubblePins]);

    const alightingsBubble = useMemo(() => {
        return convertPushpinsToAzure(alightingsBubblePins);
    }, [alightingsBubblePins]);

    const boardingBubble = useMemo(() => {
        return convertPushpinsToAzure(boardBubblePins);
    }, [boardBubblePins]);

    const isHeatMode = mapMode === 'heat';

    const heatSourcePins = useMemo(() => {
        if (heatMetric === 'boarding') {
            return boardBubblePins || [];
        }
        if (heatMetric === 'alighting') {
            return alightingsBubblePins || [];
        }
        return loadBubblePins || [];
    }, [heatMetric, boardBubblePins, alightingsBubblePins, loadBubblePins]);

    const heatFeatures = useMemo(() => {
        if (!isHeatMode) {
            return [] as atlasData.Feature<atlasData.Point, any>[];
        }

        const points = (heatSourcePins || [])
            .map(pin => {
                const lat = Number(pin?.location?.[0]);
                const lon = Number(pin?.location?.[1]);
                if (!Number.isFinite(lat) || !Number.isFinite(lon)) {
                    return null;
                }

                const value = getMetricValue(pin.options, heatMetric);
                return { pin, lat, lon, value };
            })
            .filter((point): point is { pin: PushpinProps; lat: number; lon: number; value: number } => point !== null);

        const maxValue = points.reduce((max, point) => {
            return point.value > max ? point.value : max;
        }, 0);
        const normalizer = maxValue > 0 ? maxValue : 1;

        return points.map(({ pin, lat, lon, value }) => {
            return new atlasData.Feature(
                new atlasData.Point([lon, lat]),
                {
                    ...pin.options,
                    heatValue: value,
                    heatWeight: value / normalizer,
                },
            );
        });
    }, [isHeatMode, heatSourcePins, heatMetric]);

    const lineFeatures = useMemo(() => {
        return convertPolylines(polylinesProp);
    }, [polylinesProp]);

    const popups = useMemo(() => {
        return infoboxesProp?.map((infobox, idx) => {
            const [lat, lon] = infobox.location;
            const title = infobox.title ?? infobox.vehicleId ?? 'Details';
            return (
                <AzureMapPopup
                    key={`popup-${idx}`}
                    isVisible={true}
                    options={{
                        position: [lon, lat],
                        closeButton: infobox.options?.showCloseButton ?? false,
                        pixelOffset: (infobox.options?.offset ?? [0, -20]) as [number, number],
                    }}
                    popupContent={<div className="popup-container" style={{
                        padding: '12px',
                    }}>
                        <div className="popup-title">{title}</div>
                        <div className="popup-text">{infobox.text}</div>
                    </div>}
                />
            );
        });
    }, [infoboxesProp]);

    const formatValue = React.useCallback((value?: number | null) => {
        if (value === null || value === undefined) {
            return '—';
        }
        if (typeof value === 'number' && Number.isFinite(value)) {
            return value.toLocaleString(undefined, { maximumFractionDigits: 1 });
        }
        const parsed = Number(value);
        if (Number.isFinite(parsed)) {
            return parsed.toLocaleString(undefined, { maximumFractionDigits: 1 });
        }
        return '—';
    }, []);

    const handleLineHover = React.useCallback((e: any) => {
        const shape = e?.shapes?.[0];
        if (!shape) {
            setLineHoverPopup(null);
            return;
        }
        const props = shape?.getProperties ? shape.getProperties() : shape?.properties || {};
        let position: [number, number] | undefined;
        const eventPos = e?.position;
        if (Array.isArray(eventPos) && typeof eventPos[0] === 'number' && typeof eventPos[1] === 'number') {
            position = eventPos as [number, number];
        } else {
            const coords = shape?.getCoordinates ? shape.getCoordinates() : shape?.geometry?.coordinates;
            if (Array.isArray(coords) && coords.length > 0) {
                const midIndex = Math.floor(coords.length / 2);
                const mid = coords[midIndex] || coords[0];
                if (Array.isArray(mid) && typeof mid[0] === 'number' && typeof mid[1] === 'number') {
                    position = mid as [number, number];
                }
            }
        }
        if (!position) {
            setLineHoverPopup(null);
            return;
        }
        setLineHoverPopup({
            position,
            props,
        });
    }, []);

    const handleLineLeave = React.useCallback(() => {
        setLineHoverPopup(null);
    }, []);

    const clearStopHoverPopup = React.useCallback(() => {
        if (stopHoverKeyRef.current !== null) {
            stopHoverKeyRef.current = null;
            setStopHoverPopup(null);
        }
    }, []);

    const updateStopHoverPopup = React.useCallback((shape: any, positionOverride?: [number, number]) => {
        if (!showStopHoverPopup) {
            return;
        }
        if (!shape) {
            clearStopHoverPopup();
            return;
        }
        const props = shape?.getProperties ? shape.getProperties() : shape?.properties || {};
        const stopId = props?.stopId;
        if (!stopId) {
            clearStopHoverPopup();
            return;
        }
        let position: [number, number] | undefined = positionOverride;
        if (!position) {
            const coords = shape?.getCoordinates ? shape.getCoordinates() : shape?.geometry?.coordinates;
            if (Array.isArray(coords) && typeof coords[0] === 'number' && typeof coords[1] === 'number') {
                position = [coords[0], coords[1]];
            }
        }
        if (!position) {
            clearStopHoverPopup();
            return;
        }
        const key = `${stopId}_${position[0]}_${position[1]}`;
        if (stopHoverKeyRef.current === key) {
            return;
        }
        stopHoverKeyRef.current = key;
        setStopHoverPopup({
            position,
            props,
        });
    }, [showStopHoverPopup, clearStopHoverPopup]);

    const hoverPopup = React.useMemo(() => {
        const props = lineHoverPopup?.props || {};
        const aggregationLabel = props.aggregation || 'Sum';
        const position = lineHoverPopup?.position || defaultCenter;
        return (
            <AzureMapPopup
                key="line-hover-popup"
                isVisible={!!lineHoverPopup}
                options={{
                    position,
                    closeButton: false,
                    pixelOffset: [0, -12],
                }}
                popupContent={<div className="popup-container" style={{ padding: '10px' }}>
                    <div className="popup-title">{props.routeLabel || 'Route segment'}</div>
                    <div className="popup-text" style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <span>Aggregation: {aggregationLabel}</span>
                        <span>Boardings: {formatValue(props.boarding)}</span>
                        <span>Alightings: {formatValue(props.alighting)}</span>
                        <span>Load: {formatValue(props.load)}</span>
                    </div>
                </div>}
            />
        );
    }, [lineHoverPopup, formatValue, defaultCenter]);

    const stopHoverInfoPopup = React.useMemo(() => {
        if (!showStopHoverPopup || !stopHoverPopup) {
            return null;
        }
        const props = stopHoverPopup.props || {};
        const position = stopHoverPopup.position || defaultCenter;
        const stopLabel = props.stopName || props.title || props.stopId || 'Stop';
        return (
            <AzureMapPopup
                key="stop-hover-popup"
                isVisible={true}
                options={{
                    position,
                    closeButton: false,
                    pixelOffset: [0, -15],
                }}
                popupContent={<div className="popup-container" style={{ padding: '10px' }}>
                    <div className="popup-title">{stopLabel}</div>
                    <div className="popup-text" style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {props.stopId && <span>Stop ID: {props.stopId}</span>}
                        <span>Boardings: {formatValue(props.boarding)}</span>
                        <span>Alightings: {formatValue(props.alighting)}</span>
                        <span>Load: {formatValue(props.load)}</span>
                    </div>
                </div>}
            />
        );
    }, [showStopHoverPopup, stopHoverPopup, formatValue, defaultCenter]);

    const vehicleFeatures = useMemo(() => {
        return vehiclesProp?.map((p) => {
            const [lat, lon] = p.location;
            const coordinates: [number, number] = [lon, lat];
            return new atlasData.Feature(
                new atlasData.Point(coordinates),
                {
                    ...p.options,
                    ...p.eventHandlers,
                },
            );
        });
    }, [vehiclesProp]);

    const autoBounds = useMemo<MapBounds | null>(() => {
        if (disableAutoCamera) {
            return null;
        }

        let bounds: MapBounds | null = null;
        const extend = (lat?: number, lon?: number) => {
            if (typeof lat !== 'number' || typeof lon !== 'number' || Number.isNaN(lat) || Number.isNaN(lon)) {
                return;
            }
            if (!bounds) {
                bounds = { north: lat, south: lat, east: lon, west: lon };
                return;
            }
            bounds = {
                north: Math.max(bounds.north, lat),
                south: Math.min(bounds.south, lat),
                east: Math.max(bounds.east, lon),
                west: Math.min(bounds.west, lon),
            };
        };
        const addFromPins = (pins?: PushpinProps[]) => {
            (pins || []).forEach(pin => {
                if (pin?.location) {
                    extend(pin.location[0], pin.location[1]);
                }
            });
        };
        addFromPins(pushpinsProp);
        addFromPins(loadBubblePins);
        addFromPins(alightingsBubblePins);
        addFromPins(boardBubblePins);
        (polylinesProp || []).forEach(poly => {
            (poly.locations || []).forEach(location => extend(location[0], location[1]));
        });
        (vehiclesProp || []).forEach(vehicle => {
            if (vehicle?.location) {
                extend(vehicle.location[0], vehicle.location[1]);
            }
        });
        return bounds;
    }, [pushpinsProp, loadBubblePins, alightingsBubblePins, boardBubblePins, polylinesProp, vehiclesProp, disableAutoCamera]);

    React.useEffect(() => setLineHoverPopup(null), [lineFeatures]);

    const triggerStopClick = React.useCallback((shape: any) => {
        if (!onStopClick) {
            return;
        }
        const props = shape?.getProperties ? shape.getProperties() : shape?.properties || {};
        const coords = shape?.getCoordinates ? shape.getCoordinates() : shape?.geometry?.coordinates;
        const lon = Array.isArray(coords) ? coords[0] : undefined;
        const lat = Array.isArray(coords) ? coords[1] : undefined;
        onStopClick({
            stopId: props?.stopId,
            stopName: props?.stopName ?? props?.title,
            boarding: props?.boarding,
            alighting: props?.alighting,
            load: props?.load,
            title: props?.title,
            lat,
            lon,
            routeName: props?.routeName,
            routeId: props?.routeId,
        });
    }, [onStopClick]);

    const openStopHoverPopupFromEvent = React.useCallback((shape: any, e?: any) => {
        if (!showStopHoverPopup) {
            return;
        }
        const eventPos = e?.position;
        if (Array.isArray(eventPos) && typeof eventPos[0] === 'number' && typeof eventPos[1] === 'number') {
            updateStopHoverPopup(shape, eventPos as [number, number]);
        } else {
            updateStopHoverPopup(shape);
        }
    }, [showStopHoverPopup, updateStopHoverPopup]);

    const mapOptions: IAzureMapOptions = {
        ...(map ?? {}),
        center: (map?.center as [number, number] | undefined) ?? defaultCenter,
        zoom: map?.zoom ?? defaultZoom,
        authOptions: map?.authOptions ?? {
            authType: AuthenticationType.subscriptionKey,
            subscriptionKey: config.AzureMapKey,
        },
        style: map?.style ?? 'road',
        showLogo: map?.showLogo ?? false,
        showFeedbackLink: map?.showFeedbackLink ?? false,
        showTileBoundaries: map?.showTileBoundaries ?? false,
    };

    const mapControls: IAzureMapControls[] | undefined = React.useMemo(() => {
        if (!showZoomControls) {
            return undefined;
        }

        return [{
            controlName: 'ZoomControl',
            options: {
                position: ControlPosition.BottomRight,
            },
        }];
    }, [showZoomControls]);

    useEffect(() => {
        if (!showStopHoverPopup || !isMapReady || !mapRef) {
            return;
        }

        const handleMapMove = (e: any) => {
            const shapes = (e?.shapes || []) as any[];
            const stopShape = shapes.find(shape => {
                const props = shape?.getProperties ? shape.getProperties() : shape?.properties || {};
                return props?.stopId;
            });
            if (stopShape) {
                const eventPos = e?.position;
                if (Array.isArray(eventPos) && typeof eventPos[0] === 'number' && typeof eventPos[1] === 'number') {
                    updateStopHoverPopup(stopShape, eventPos as [number, number]);
                } else {
                    updateStopHoverPopup(stopShape);
                }
                return;
            }
            clearStopHoverPopup();
        };

        mapRef.events.add('mousemove', handleMapMove as any);
        mapRef.events.add('mouseout', handleMapMove as any);

        return () => {
            mapRef.events.remove('mousemove', handleMapMove as any);
            mapRef.events.remove('mouseout', handleMapMove as any);
        };
    }, [showStopHoverPopup, isMapReady, mapRef, updateStopHoverPopup, clearStopHoverPopup]);

    return (
        <div
            style={{
                width: '100%',
                height: '100%',
            }}
            onMouseLeave={() => {
                setLineHoverPopup(null);
                clearStopHoverPopup();
            }}
        >
            <AzureMapsProvider>
                <AzureMap options={mapOptions} controls={mapControls}>
                    <AzureMapCameraControl
                        bounds={autoBounds}
                        padding={cameraPadding ?? DEFAULT_CAMERA_PADDING}
                        maxZoom={cameraMaxZoom}
                        disabled={disableAutoCamera}
                    />
                    <AzureMapSpriteRegistrar/>
                    <AzureMapDataSourceProvider id="lineDataSource">
                        <AzureMapLayerProvider
                            id="lineLayer"
                            type="LineLayer"
                            options={{
                                strokeColor: ['coalesce', ['get', 'strokeColor'], '#026db5'] as any,
                                strokeWidth: ['coalesce', ['get', 'strokeWidth'], 3] as any,
                            }}
                        />
                    </AzureMapDataSourceProvider>
                    <AzureMapDataSourceProvider id="line2DataSource" collection={lineFeatures}>
                        <AzureMapLayerProvider
                            id="line2Layer"
                            type="LineLayer"
                            options={{
                                strokeColor: ['coalesce', ['get', 'strokeColor'], '#026db5'] as any,
                                strokeWidth: ['coalesce', ['get', 'strokeWidth'], 3] as any,
                            }}
                            events={{
                                mousemove: handleLineHover,
                                mouseout: handleLineLeave,
                            }}
                        />
                    </AzureMapDataSourceProvider>
                    <AzureMapDataSourceProvider id="markerDataSource" collection={convertedFeatures} options={{ cluster: false }}>
                        <AzureMapLayerProvider
                            id="markerLayer"
                            type="SymbolLayer"
                            options={{
                                minZoom: 0,
                                maxZoom: 24,
                                iconOptions: {
                                    ignorePlacement: true,
                                    iconAllowOverlap: true,
                                    image: [
                                        'case',
                                        ['==', ['get', 'icon'], 'stopSelectedIcon'], 'stopSelectedIcon',
                                        ['==', ['get', 'icon'], 'stopIcon'], 'stopIcon',
                                        ['==', ['get', 'icon'], 'busRed'], 'busRed',
                                        ['==', ['get', 'icon'], 'busRedWhite'], 'busRedWhite',
                                        ['==', ['get', 'icon'], 'busBlue'], 'busBlue',
                                        ['==', ['get', 'icon'], 'bus-pushpin'], 'bus-pushpin',
                                        ['==', ['get', 'color'], 'green'], 'pin-round-blue',
                                        ['==', ['get', 'color'], 'red'], 'pin-round-red',
                                        'pin-round-blue',
                                    ],
                                    offset: SYMBOL_MARKER_OFFSET_EXPRESSION,
                                    anchor: 'center',
                                },
                                textOptions: {
                                    textField: ['get', 'title'],
                                    allowOverlap: true,
                                    ignorePlacement: true,
                                },
                            }}
                            events={{
                                click: (e: any) => {
                                    const shape = e?.shapes?.[0];
                                    triggerStopClick(shape);
                                },
                            }}
                        />
                    </AzureMapDataSourceProvider>
                    {isHeatMode
                        ? (
                            <AzureMapDataSourceProvider id="heatPinsDataSource" collection={heatFeatures}>
                                <AzureMapLayerProvider
                                    id="heatPinsLayer"
                                    type="HeatLayer"
                                    options={{
                                        filter: ['==', ['geometry-type'], 'Point'] as any,
                                        weight: ['coalesce', ['get', 'heatWeight'], 0] as any,
                                        radius: HEAT_RADIUS_BY_ZOOM,
                                        intensity: HEAT_INTENSITY_BY_ZOOM,
                                        opacity: 0.85,
                                        color: HEAT_COLOR_RAMP,
                                    }}
                                />
                                <AzureMapLayerProvider
                                    id="heatInteractionLayer"
                                    type="BubbleLayer"
                                    options={{
                                        radius: 8,
                                        color: 'rgba(0,0,0,0)',
                                        strokeWidth: 0,
                                        opacity: 0.01,
                                    }}
                                    events={{
                                        click: (e: any) => {
                                            const shape = e?.shapes?.[0];
                                            triggerStopClick(shape);
                                            openStopHoverPopupFromEvent(shape, e);
                                        },
                                        mouseout: () => {
                                            clearStopHoverPopup();
                                        },
                                    }}
                                />
                            </AzureMapDataSourceProvider>
                        )
                        : (
                            <>
                                <AzureMapDataSourceProvider id="bubblePinsDataSource" collection={loadBubble}>
                                    <AzureMapLayerProvider
                                        id="bubblePinsLayer"
                                        type="BubbleLayer"
                                        options={{
                                            visible: showLoadLayer,
                                            radius: ['coalesce', ['get', 'radius'], 7] as any,
                                            color: ['get', 'color'],
                                            strokeColor: '#ffffff',
                                            strokeWidth: 1,
                                            blur: 0,
                                            opacity: 0.95,
                                        }}
                                        events={{
                                            click: (e: any) => {
                                                const shape = e?.shapes?.[0];
                                                triggerStopClick(shape);
                                                openStopHoverPopupFromEvent(shape, e);
                                            },
                                            mouseout: () => {
                                                clearStopHoverPopup();
                                            },
                                        }}
                                    />
                                </AzureMapDataSourceProvider>

                                <AzureMapDataSourceProvider id="boardBubblePinsDataSource" collection={boardingBubble}>
                                    <AzureMapLayerProvider
                                        id="boardBubblePinsLayer"
                                        type="BubbleLayer"
                                        options={{
                                            visible: showBoardingsLayer,
                                            radius: ['coalesce', ['get', 'radius'], 1] as any,
                                            color: '#2ecc71',
                                            strokeColor: '#ffffff',
                                            strokeWidth: 1,
                                            blur: 0,
                                            opacity: 0.9,
                                        }}
                                        events={{
                                            click: (e: any) => {
                                                const shape = e?.shapes?.[0];
                                                triggerStopClick(shape);
                                                openStopHoverPopupFromEvent(shape, e);
                                            },
                                            mouseout: () => {
                                                clearStopHoverPopup();
                                            },
                                        }}
                                    />
                                </AzureMapDataSourceProvider>

                                <AzureMapDataSourceProvider id="overlayBubblePinsDataSource" collection={alightingsBubble}>
                                    <AzureMapLayerProvider
                                        id="overlayBubblePinsLayer"
                                        type="BubbleLayer"
                                        options={{
                                            visible: showAlightingsLayer,
                                            radius: ['coalesce', ['get', 'radius'], 1] as any,
                                            color: '#f39c12',
                                            strokeColor: '#ffffff',
                                            strokeWidth: 1,
                                            blur: 0,
                                            opacity: 0.9,
                                        }}
                                        events={{
                                            click: (e: any) => {
                                                const shape = e?.shapes?.[0];
                                                triggerStopClick(shape);
                                                openStopHoverPopupFromEvent(shape, e);
                                            },
                                            mouseout: () => {
                                                clearStopHoverPopup();
                                            },
                                        }}
                                    />
                                </AzureMapDataSourceProvider>
                            </>
                        )}

                    <AzureMapDataSourceProvider id="busDataSource" collection={vehicleFeatures}>
                        <AzureMapLayerProvider
                            id="busLayer"
                            type="SymbolLayer"
                            options={{
                                iconOptions: {
                                    ignorePlacement: true,
                                    iconAllowOverlap: true,
                                    image: [
                                        'case',
                                        ['==', ['get', 'icon'], 'stopSelectedIcon'], 'stopSelectedIcon',
                                        ['==', ['get', 'icon'], 'stopIcon'], 'stopIcon',
                                        ['==', ['get', 'icon'], 'busRed'], 'busRed',
                                        ['==', ['get', 'icon'], 'busRedWhite'], 'busRedWhite',
                                        ['==', ['get', 'icon'], 'busBlue'], 'busBlue',
                                        ['==', ['get', 'icon'], 'bus-pushpin'], 'bus-pushpin',
                                        ['==', ['get', 'color'], 'green'], 'pin-round-blue',
                                        ['==', ['get', 'color'], 'red'], 'pin-round-red',
                                        'pin-round-blue',
                                    ],
                                    offset: [0, 0],
                                    anchor: 'center',
                                },
                                textOptions: {
                                    textField: ['get', 'title'],
                                },
                            }}
                        />
                    </AzureMapDataSourceProvider>

                    {!isHeatMode
                        ? (
                            <AzureMapSelectionDrawingControl
                                onSelectionChanged={onSelectionChanged}
                                selectionSourceId={selectionSourceId}
                                shapeSelectionMode={ShapeSelectionMode.point}
                                selectionResetToken={selectionResetToken}
                                emitSelectionArea={emitSelectionArea}
                            />
                        )
                        : <></>}

                    <>{popups}</>
                    <>{showRouteSegmentPopup ? hoverPopup : null}</>
                    <>{showStopHoverPopup ? stopHoverInfoPopup : null}</>
                </AzureMap>
            </AzureMapsProvider>
        </div>
    );
};

export default AzureMapSelectionMap;
